import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'

import { POST, httpOk } from 'helpers/api'

import { dimensionKey, dimensionLabel, formatMeasure } from './pivot'

// Фильтр по значениям поля — воронка на плашке, как в iiko.
// Значения — только те, что встречаются в продажах периода с учётом остальных фильтров.
// Ничего не отмечено — фильтра нет (все значения).
function FilterModal({ field, title, request, selected, measureLabel, onApply, onClose }) {
	const { t } = useTranslation()

	const [rows, setRows] = useState(null)
	const [measure, setMeasure] = useState(null)
	const [checked, setChecked] = useState(new Set(selected || []))
	const [search, setSearch] = useState('')

	async function load() {
		const response = await POST('/report-olap/values', { ...request, dimension: field }, { notification: false })
		if (httpOk(response)) {
			const list = response.data.rows || []
			const valueMeasure = response.data.measure || null
			setMeasure(valueMeasure)
			setRows(list.map(row => ({ key: dimensionKey(row, field), label: dimensionLabel(row, field, t), value: valueMeasure ? row[valueMeasure] : null })))
		} else {
			setRows([])
		}
	}

	useEffect(() => {
		load()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	// Отмеченные значения, которых в этом периоде нет, не теряем — показываем внизу
	const options = useMemo(() => {
		if (!rows) return []
		const known = new Set(rows.map(row => row.key))
		const missing = [...checked].filter(key => !known.has(key)).map(key => ({ key, label: key === '' ? t('olap_empty_value') : key, value: null }))
		const list = [...rows, ...missing]
		const query = search.trim().toLowerCase()
		return query ? list.filter(row => row.label.toLowerCase().includes(query)) : list
	}, [rows, search]) // eslint-disable-line react-hooks/exhaustive-deps

	function toggle(key) {
		const next = new Set(checked)
		next.has(key) ? next.delete(key) : next.add(key)
		setChecked(next)
	}

	function selectShown(bool) {
		const next = new Set(checked)
		options.forEach(row => bool ? next.add(row.key) : next.delete(row.key))
		setChecked(next)
	}

	return (
		<Modal show={true} animation={false} centered scrollable onHide={onClose}>
			<Modal.Header closeButton>
				<Modal.Title>{t('olap_filter_title')}: {title}</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ height: '480px' }}>
				<input type="text" className="form-control mb-2" autoFocus value={search}
					placeholder={t('search')}
					onChange={(e) => setSearch(e.target.value)} />
				<div className="d-flex gap-2 mb-2">
					<button type="button" className="btn btn-sm btn-outline-primary" onClick={() => selectShown(true)}>{t('olap_select_all')}</button>
					<button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => selectShown(false)}>{t('olap_clear')}</button>
					{measure && <span className="ms-auto text-muted small vertical-center">{measureLabel(measure)}</span>}
				</div>
				{rows === null && <div className="text-muted text-center py-4">{t('olap_values_loading')}</div>}
				{rows !== null && options.length === 0 && <div className="text-muted text-center py-4">{t('olap_values_none')}</div>}
				{options.map(row => (
					<label key={row.key} className="d-flex align-items-center gap-2 py-1 border-bottom" style={{ cursor: 'pointer' }}>
						<input type="checkbox" className="form-check-input m-0" checked={checked.has(row.key)} onChange={() => toggle(row.key)} />
						<span className="flex-grow-1">{row.label}</span>
						{row.value !== null && <span className="text-muted small">{formatMeasure(measure, row.value)}</span>}
					</label>
				))}
			</Modal.Body>
			<Modal.Footer>
				<div className="d-flex w-100 gap-2">
					<span className="text-muted small vertical-center me-auto">
						{checked.size > 0 ? t('olap_filtered', { count: checked.size }) : t('olap_filter_all')}
					</span>
					<button type="button" className="btn btn-outline-warning" onClick={onClose}>{t('cancel')}</button>
					<button type="button" className="btn btn-primary" onClick={() => onApply([...checked])}>{t('apply')}</button>
				</div>
			</Modal.Footer>
		</Modal>
	)
}

export default FilterModal
